<template>
  <div class="schedule" v-if="companyStore.currentCompany">
    <section class="settings-card">
      <div class="settings-card__header">
        <h3>{{ $t("custom_schedule") }}</h3>
      </div>
      <p class="settings-card__hint">{{ $t("custom_schedule_toggle_hint") }}</p>

      <VSwitch
        v-model="companyStore.currentCompany.custom_schedule"
        :label="$t('custom_schedule')"
        @change="saveScheduleMode"
        inset
        color="success"
      />

      <template v-if="!currentCompany!.custom_schedule">
        <WeeklyScheduleCalendar
          ref="weeklyCalendarRef"
          :schedules="currentCompany!.schedules_attributes"
        />
        <div class="schedule__savebar">
          <span
            class="schedule__savebar-status"
            :class="{ 'is-saved': weeklySaved }"
          >
            {{ weeklySaved ? $t("settings.all_saved") : "" }}
          </span>
          <VBtn
            color="#403838"
            variant="flat"
            :loading="savingWeekly"
            @click="saveWeeklySchedule"
          >
            {{ $t("save") }}
          </VBtn>
        </div>
      </template>
      <template v-else>
        <CustomScheduleCalendar
          ref="customCalendarRef"
          :schedules="currentCompany!.schedules_attributes"
        />
        <div class="schedule__savebar">
          <span
            class="schedule__savebar-status"
            :class="{ 'is-saved': customSaved }"
          >
            {{ customSaved ? $t("settings.all_saved") : "" }}
          </span>
          <VBtn
            color="#403838"
            variant="flat"
            :loading="savingCustom"
            @click="saveCustomSchedule"
          >
            {{ $t("save") }}
          </VBtn>
        </div>
      </template>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import WeeklyScheduleCalendar from "./weekly-schedule-calendar.vue";
import CustomScheduleCalendar from "./custom-schedule-calendar.vue";
import { useCompanyStore } from "@/main/stores/company.store";
import { WorkingHoursSchedule } from "@/main/types/working-hours-schedule.type";
import { updateCompany } from "@/main/services/api.service";

const companyStore = useCompanyStore();

const currentCompany = computed(() => companyStore.currentCompany);

const weeklyCalendarRef = ref<InstanceType<typeof WeeklyScheduleCalendar>>();
const customCalendarRef = ref<InstanceType<typeof CustomScheduleCalendar>>();

const savingWeekly = ref(false);
const savingCustom = ref(false);
const weeklySaved = ref(false);
const customSaved = ref(false);

const persist = async (schedules_attributes?: WorkingHoursSchedule[]) => {
  if (!currentCompany.value) return;

  try {
    const company = await updateCompany(currentCompany.value.id, {
      custom_schedule: currentCompany.value.custom_schedule,
      schedules_attributes,
    });

    companyStore.$patch({
      currentCompany: company,
    });
  } catch (error) {
    console.error(error);
  }
};

// Just toggling weekly/custom mode - resubmit the schedule as currently
// loaded (untouched) alongside the new custom_schedule flag.
const saveScheduleMode = async () => {
  if (!currentCompany.value) return;
  await persist(currentCompany.value.schedules_attributes);
};

const saveWeeklySchedule = async () => {
  if (!weeklyCalendarRef.value) return;
  savingWeekly.value = true;
  await persist(weeklyCalendarRef.value.getScheduleAttributes());
  weeklyCalendarRef.value.resetFromSchedules(
    companyStore.currentCompany?.schedules_attributes
  );
  savingWeekly.value = false;
  weeklySaved.value = true;
  setTimeout(() => {
    weeklySaved.value = false;
  }, 1500);
};

const saveCustomSchedule = async () => {
  if (!customCalendarRef.value) return;
  savingCustom.value = true;
  await persist(customCalendarRef.value.getScheduleAttributes());
  customCalendarRef.value.resetFromSchedules(
    companyStore.currentCompany?.schedules_attributes
  );
  savingCustom.value = false;
  customSaved.value = true;
  setTimeout(() => {
    customSaved.value = false;
  }, 1500);
};
</script>

<style lang="scss" scoped>
.schedule {
  max-width: 720px;

  :deep(.v-input__details) {
    display: none;
  }

  &__savebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 12px;
    margin-top: 16px;
  }

  &__savebar-status {
    font-size: 13px;
    color: transparent;

    &.is-saved {
      color: #2e7d32;
    }
  }
}

.settings-card {
  background: #f7f8fa;
  border: 1px solid #eceef1;
  border-radius: 16px;
  padding: 24px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;

    h3 {
      color: #403838;
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
  }

  &__hint {
    margin: 0 0 8px;
    color: #696e7c;
    font-size: 12px;
    line-height: 16px;
  }
}
</style>
